// Prices (sculpt-api PLAN-multi-currency.md §2b, §2c): the currencies staff sell in, their rates, and every offer
// and pack key priced in each currency, edited one currency's column at a time. A rate never converts a price: each
// currency's prices are set here by hand; the rate turns a request's euro price into the currency (the USD one also
// Anthropic's dollars into euros), and a change reprices the requests after it, never a balance.
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime, money, toDecimal } from '../../services/format';
import { Alert, Badge, Button, Card, Cell, Checkbox, Confirm, Field, Input, PageTitle, Row, Table } from '../../components/ui';
import type { CurrencyOut, Decimal, OfferPrices, OfferPricesIn, PackPricesIn, PriceTable, PriceTableOffer } from '../../types/api';

const EUR = 'EUR';

/** A rate for the API from a form input (up to 6 decimals, above zero); null when blank or not a number. */
export function toRate(input: string): string | null {
  const t = input.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  const r = Number(n.toFixed(6));
  return r > 0 ? String(r) : null;
}

/** A rate as staff read it: "0.86", not "0.860000". */
function showRate(rate: Decimal): string {
  const n = Number(rate);
  return Number.isNaN(n) ? rate : String(n);
}

/** How many tier rows an offer has: as many as its euro offer (tiers match by rank), else the most any currency has. */
export function tierCount(o: PriceTableOffer): number {
  const eur = o.prices[EUR];
  if (eur) return eur.credit_tiers?.length ?? 0;
  return Math.max(0, ...Object.values(o.prices).map((p) => p.credit_tiers?.length ?? 0));
}

/** The keys on sale in euros (public and active) that `currency` has no price for. */
export function unpricedOnSale(table: PriceTable, currency: string): string[] {
  const missing = (prices: Record<string, { public: boolean | null; active: boolean | null }>) => {
    const eur = prices[EUR];
    return Boolean(eur?.public && eur.active && !prices[currency]);
  };
  return [...table.offers.filter((o) => missing(o.prices)).map((o) => o.name), ...table.packs.filter((p) => missing(p.prices)).map((p) => p.name)];
}

// ---- currencies ----

function CurrencyRow({ c, onSaved, onDeleted }: { c: CurrencyOut; onSaved: (list: CurrencyOut[]) => void; onDeleted: (list: CurrencyOut[]) => void }) {
  const { t, i18n } = useTranslation();
  const [rate, setRate] = useState(showRate(c.rate));
  const [onSale, setOnSale] = useState(c.on_sale);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  useEffect(() => {
    setRate(showRate(c.rate));
    setOnSale(c.on_sale);
  }, [c.rate, c.on_sale]);
  const parsed = toRate(rate);
  const dirty = parsed !== showRate(c.rate) || onSale !== c.on_sale;

  const save = async () => {
    if (!parsed) {
      setFail(t('prices.badRate'));
      return;
    }
    setBusy(true);
    setFail(null);
    try {
      onSaved(await api.setCurrency(c.code, { rate: parsed, on_sale: onSale }));
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Only a currency nothing uses goes (the API says why otherwise); USD stays, its rate also prices Anthropic's dollars.
  const remove = async () => {
    setBusy(true);
    setFail(null);
    try {
      onDeleted(await api.deleteCurrency(c.code));
    } catch (err) {
      setFail(errorMessage(err));
      setConfirming(false);
      setBusy(false);
    }
  };

  if (c.fixed)
    return (
      <Row>
        <Cell mono>{c.code}</Cell>
        <Cell mono>
          1 <Badge>{t('prices.fixed')}</Badge>
        </Cell>
        <Cell>
          <Badge tone="good">{t('prices.onSale')}</Badge>
        </Cell>
        <Cell mono>—</Cell>
        <Cell>{null}</Cell>
      </Row>
    );
  return (
    <Row>
      <Cell mono>{c.code}</Cell>
      <Cell>
        <Input className="w-28" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} aria-label={t('prices.rate')} data-testid={`rate-${c.code}`} />
      </Cell>
      <Cell>
        <Checkbox label={t('prices.onSale')} checked={onSale} onChange={(e) => setOnSale(e.target.checked)} data-testid={`onsale-${c.code}`} />
      </Cell>
      <Cell mono>{dateTime(c.updated_at, i18n.language)}</Cell>
      <Cell>
        <div className="flex items-center gap-2">
          <Button variant="primary" disabled={!dirty} busy={busy} onClick={() => void save()} data-testid={`save-currency-${c.code}`}>
            {t('common.save')}
          </Button>
          {c.code !== 'USD' && (
            <Button variant="ghost" disabled={c.on_sale || busy} title={c.on_sale ? t('prices.deleteOnSale') : undefined} onClick={() => setConfirming(true)} data-testid={`delete-currency-${c.code}`}>
              {t('prices.delete')}
            </Button>
          )}
          {fail && <span className="text-[11.5px] text-danger">{fail}</span>}
        </div>
        <Confirm
          open={confirming}
          danger
          title={t('prices.deleteTitle', { code: c.code })}
          message={t('prices.deleteMessage')}
          confirmLabel={t('prices.delete')}
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setConfirming(false)}
        />
      </Cell>
    </Row>
  );
}

function CurrenciesCard({ currencies, onSaved, onDeleted }: { currencies: CurrencyOut[]; onSaved: (list: CurrencyOut[]) => void; onDeleted: (list: CurrencyOut[]) => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    const r = toRate(rate);
    if (!/^[A-Z]{3}$/.test(c)) return setFail(t('prices.badCode'));
    if (currencies.some((x) => x.code === c)) return setFail(t('prices.codeListed', { code: c }));
    if (!r) return setFail(t('prices.badRate'));
    setBusy(true);
    setFail(null);
    try {
      onSaved(await api.setCurrency(c, { rate: r, on_sale: false }));
      setCode('');
      setRate('');
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t('prices.currencies')}>
      <p className="mb-3 text-[12px] text-muted">{t('prices.rateHint')}</p>
      <Table head={[t('prices.currency'), t('prices.rate'), t('prices.onSale'), t('prices.updated'), '']}>
        {currencies.map((c) => (
          <CurrencyRow key={c.code} c={c} onSaved={onSaved} onDeleted={onDeleted} />
        ))}
      </Table>
      <p className="mt-2 text-[11.5px] text-warn">{t('prices.rateChangeHint')}</p>
      <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-2">
        <Field label={t('prices.newCode')}>
          <Input className="w-24 uppercase" maxLength={3} value={code} onChange={(e) => setCode(e.target.value)} data-testid="new-currency-code" />
        </Field>
        <Field label={t('prices.rate')}>
          <Input className="w-28" inputMode="decimal" placeholder="0.86" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="new-currency-rate" />
        </Field>
        <Button type="submit" busy={busy} data-testid="add-currency">
          {t('prices.addCurrency')}
        </Button>
        <span className="pb-2 text-[11.5px] text-faint">{t('prices.addHint')}</span>
      </form>
      {fail && (
        <div className="mt-2">
          <Alert>{fail}</Alert>
        </div>
      )}
    </Card>
  );
}

// ---- the price table ----

interface TierDraft {
  price: string;
  usage: string;
}

interface OfferDraft {
  price: string;
  allowance: string;
  cap: string;
  tiers: TierDraft[];
}

interface PackDraft {
  price: string;
  credit: string;
}

interface Draft {
  offers: Record<string, OfferDraft>;
  packs: Record<string, PackDraft>;
}

function offerDraft(o: PriceTableOffer, currency: string): OfferDraft {
  const p = o.prices[currency];
  const n = tierCount(o);
  return {
    price: p?.price_per_seat ?? '',
    allowance: p?.allowance_per_seat_eur ?? '',
    cap: p?.user_cap_eur ?? '',
    tiers: Array.from({ length: n }, (_, i) => ({ price: p?.credit_tiers?.[i]?.price ?? '', usage: p?.credit_tiers?.[i]?.usage ?? '' })),
  };
}

function draftOf(table: PriceTable, currency: string): Draft {
  return {
    offers: Object.fromEntries(table.offers.map((o) => [o.key, offerDraft(o, currency)])),
    packs: Object.fromEntries(table.packs.map((p) => [p.key, { price: p.prices[currency]?.price ?? '', credit: p.prices[currency]?.credit_eur ?? '' }])),
  };
}

const blank = (s: string) => !s.trim();
const sameOffer = (a: OfferDraft, b: OfferDraft) => JSON.stringify(a) === JSON.stringify(b);

class Incomplete extends Error {}

/** The column staff edited as the API takes it: rows left wholly blank and priced rows left unchanged are not sent
 *  (keys left out are untouched). A row not yet priced in the currency must give every amount its euro row has. */
export function pricesBody(table: PriceTable, currency: string, draft: Draft): { offers: OfferPricesIn[]; packs: PackPricesIn[] } {
  const before = draftOf(table, currency);
  const offers: OfferPricesIn[] = [];
  for (const o of table.offers) {
    const d = draft.offers[o.key];
    if (!d) continue;
    const priced = Boolean(o.prices[currency]);
    const empty = [d.price, d.allowance, d.cap, ...d.tiers.flatMap((x) => [x.price, x.usage])].every(blank);
    if (empty || (priced && sameOffer(d, before.offers[o.key]))) continue;
    const eur: OfferPrices | undefined = o.prices[EUR];
    const price = toDecimal(d.price);
    if (!price) throw new Incomplete(o.name);
    if (!priced && ((eur?.allowance_per_seat_eur != null && blank(d.allowance)) || (eur?.user_cap_eur != null && blank(d.cap)))) throw new Incomplete(o.name);
    const amount = (s: string) => {
      if (blank(s)) return null;
      const v = toDecimal(s);
      if (!v) throw new Incomplete(o.name);
      return v;
    };
    const tiers = d.tiers.map((x) => {
      const p = toDecimal(x.price);
      if (!p) throw new Incomplete(o.name);
      return { price: p, usage: amount(x.usage) ?? p };
    });
    offers.push({ key: o.key, price_per_seat: price, allowance_per_seat_eur: amount(d.allowance), user_cap_eur: amount(d.cap), credit_tiers: tiers.length ? tiers : null });
  }
  const packs: PackPricesIn[] = [];
  for (const p of table.packs) {
    const d = draft.packs[p.key];
    if (!d || (blank(d.price) && blank(d.credit))) continue;
    const now = p.prices[currency];
    if (now && d.price === now.price && d.credit === now.credit_eur) continue;
    const price = toDecimal(d.price);
    const credit = toDecimal(d.credit);
    if (!price || !credit) throw new Incomplete(p.name);
    packs.push({ key: p.key, price, credit_eur: credit });
  }
  return { offers, packs };
}

function PriceInput({ value, placeholder, onChange, testId, label }: { value: string; placeholder: string; onChange: (v: string) => void; testId: string; label: string }) {
  return <Input className="w-24 font-mono text-[11.5px]" inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testId} />;
}

function PriceTableCard({ table, onSaved }: { table: PriceTable; onSaved: (t: PriceTable, currency: string) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({ offers: {}, packs: {} });
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const codes = table.currencies.map((c) => c.code);

  const edit = (currency: string) => {
    setEditing(currency);
    setDraft(draftOf(table, currency));
    setFail(null);
  };
  const setOffer = (key: string, patch: Partial<OfferDraft>) => setDraft((d) => ({ ...d, offers: { ...d.offers, [key]: { ...d.offers[key], ...patch } } }));
  const setTier = (key: string, i: number, patch: Partial<TierDraft>) =>
    setDraft((d) => ({ ...d, offers: { ...d.offers, [key]: { ...d.offers[key], tiers: d.offers[key].tiers.map((x, j) => (j === i ? { ...x, ...patch } : x)) } } }));
  const setPack = (key: string, patch: Partial<PackDraft>) => setDraft((d) => ({ ...d, packs: { ...d.packs, [key]: { ...d.packs[key], ...patch } } }));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    let body: { offers: OfferPricesIn[]; packs: PackPricesIn[] };
    try {
      body = pricesBody(table, editing, draft);
    } catch (err) {
      if (err instanceof Incomplete) return setFail(t('prices.incomplete', { name: err.message }));
      throw err;
    }
    if (!body.offers.length && !body.packs.length) {
      setEditing(null);
      return;
    }
    setBusy(true);
    setFail(null);
    try {
      onSaved(await api.setPrices(editing, body), editing);
      setEditing(null);
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /** A read-only cell, or the editing column's input: prefilled, else empty with the euro value as its reference. */
  const cell = (currency: string, shown: string | null | undefined, input: () => JSX.Element) =>
    editing === currency ? (
      <Cell key={currency}>{input()}</Cell>
    ) : (
      <Cell key={currency} mono className={shown == null ? 'text-faint' : ''}>
        {shown ?? '—'}
      </Cell>
    );
  const m = (v: string | null | undefined, c: string) => (v == null ? null : money(v, c, l));
  /** The euro value shown in an empty input; for a priced row, what a blank means. */
  const reference = (priced: boolean, eurValue: string | null | undefined, whenBlank: string) => (priced ? whenBlank : eurValue != null ? money(eurValue, EUR, l) : whenBlank);

  const head = [
    '',
    ...table.currencies.map((c) => (
      <div key={c.code} className="flex items-center gap-2">
        <span>{c.code}</span>
        {!c.on_sale && <span className="normal-case tracking-normal text-fainter">{t('prices.notOnSale')}</span>}
        {editing === null && (
          <Button variant="ghost" className="h-6 px-1.5 normal-case tracking-normal" onClick={() => edit(c.code)} data-testid={`edit-col-${c.code}`}>
            {t('common.edit')}
          </Button>
        )}
      </div>
    )),
  ];

  const label = 'pl-6 text-muted';
  const rows: JSX.Element[] = [];
  const section = (title: string) => (
    <Row key={`section-${title}`} className="bg-code">
      <td colSpan={codes.length + 1} className="px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">
        {title}
      </td>
    </Row>
  );

  rows.push(section(t('prices.offers')));
  for (const o of table.offers) {
    const eur = o.prices[EUR];
    rows.push(
      <Row key={`o-${o.key}`}>
        <Cell>
          <span className="font-medium">{o.name}</span> <span className="font-mono text-[10.5px] text-faint">{o.key}</span>
        </Cell>
        {codes.map((c) => {
          const p = o.prices[c];
          return (
            <Cell key={c} mono className="text-[10.5px] text-faint">
              {p ? (
                <span data-testid={`meta-${o.key}-${c}`}>
                  {t('prices.versionMeta', { version: p.version, count: p.subscribers })}
                  {p.active === false ? ` · ${t('offers.inactive')}` : p.public === false ? ` · ${t('offers.private')}` : ''}
                </span>
              ) : editing === c ? (
                t('prices.fromEur')
              ) : null}
            </Cell>
          );
        })}
      </Row>,
    );
    rows.push(
      <Row key={`o-${o.key}-seat`}>
        <Cell className={label}>{t('offers.pricePerSeat')}</Cell>
        {codes.map((c) =>
          cell(c, m(o.prices[c]?.price_per_seat, c), () => (
            <PriceInput value={draft.offers[o.key].price} placeholder={reference(Boolean(o.prices[c]), eur?.price_per_seat, '')} onChange={(v) => setOffer(o.key, { price: v })} label={`${o.name} ${t('offers.pricePerSeat')}`} testId={`price-${o.key}-seat`} />
          )),
        )}
      </Row>,
      <Row key={`o-${o.key}-allowance`}>
        <Cell className={label}>{t('offers.allowancePerSeat')}</Cell>
        {codes.map((c) => {
          const p = o.prices[c];
          return cell(c, p ? (p.allowance_per_seat_eur === null ? t('orgs.unlimited') : m(p.allowance_per_seat_eur, c)) : null, () => (
            <PriceInput value={draft.offers[o.key].allowance} placeholder={reference(Boolean(p), eur?.allowance_per_seat_eur, t('orgs.unlimited'))} onChange={(v) => setOffer(o.key, { allowance: v })} label={`${o.name} ${t('offers.allowancePerSeat')}`} testId={`price-${o.key}-allowance`} />
          ));
        })}
      </Row>,
      <Row key={`o-${o.key}-cap`}>
        <Cell className={label}>{t('offers.userCap')}</Cell>
        {codes.map((c) => {
          const p = o.prices[c];
          return cell(c, p ? (p.user_cap_eur === null ? t('prices.noCap') : m(p.user_cap_eur, c)) : null, () => (
            <PriceInput value={draft.offers[o.key].cap} placeholder={reference(Boolean(p), eur?.user_cap_eur, t('prices.noCap'))} onChange={(v) => setOffer(o.key, { cap: v })} label={`${o.name} ${t('offers.userCap')}`} testId={`price-${o.key}-cap`} />
          ));
        })}
      </Row>,
    );
    for (let i = 0; i < tierCount(o); i++) {
      rows.push(
        <Row key={`o-${o.key}-tier-${i}`}>
          <Cell className={label}>{t('prices.tier', { n: i + 1 })}</Cell>
          {codes.map((c) => {
            const tier = o.prices[c]?.credit_tiers?.[i];
            const eurTier = eur?.credit_tiers?.[i];
            const priced = Boolean(o.prices[c]);
            return cell(c, tier ? `${money(tier.price, c, l)} → ${money(tier.usage, c, l)}` : null, () => (
              <div className="flex items-center gap-1">
                <PriceInput value={draft.offers[o.key].tiers[i].price} placeholder={reference(priced, eurTier?.price, '')} onChange={(v) => setTier(o.key, i, { price: v })} label={`${o.name} ${t('prices.tier', { n: i + 1 })} ${t('packs.price')}`} testId={`price-${o.key}-tier-${i}-price`} />
                <span className="text-faint">→</span>
                <PriceInput value={draft.offers[o.key].tiers[i].usage} placeholder={reference(priced, eurTier?.usage, '')} onChange={(v) => setTier(o.key, i, { usage: v })} label={`${o.name} ${t('prices.tier', { n: i + 1 })} ${t('packs.credit')}`} testId={`price-${o.key}-tier-${i}-usage`} />
              </div>
            ));
          })}
        </Row>,
      );
    }
  }

  rows.push(section(t('prices.packs')));
  for (const p of table.packs) {
    const eur = p.prices[EUR];
    rows.push(
      <Row key={`p-${p.key}`}>
        <Cell>
          <span className="font-medium">{p.name}</span> <span className="font-mono text-[10.5px] text-faint">{p.key}</span>
        </Cell>
        {codes.map((c) => {
          const x = p.prices[c];
          return (
            <Cell key={c} mono className="text-[10.5px] text-faint">
              {x ? (x.active === false ? t('offers.inactive') : x.public === false ? t('offers.private') : null) : editing === c ? t('prices.fromEur') : null}
            </Cell>
          );
        })}
      </Row>,
      <Row key={`p-${p.key}-price`}>
        <Cell className={label}>{t('packs.price')}</Cell>
        {codes.map((c) =>
          cell(c, m(p.prices[c]?.price, c), () => (
            <PriceInput value={draft.packs[p.key].price} placeholder={reference(Boolean(p.prices[c]), eur?.price, '')} onChange={(v) => setPack(p.key, { price: v })} label={`${p.name} ${t('packs.price')}`} testId={`pack-${p.key}-price`} />
          )),
        )}
      </Row>,
      <Row key={`p-${p.key}-credit`}>
        <Cell className={label}>{t('packs.credit')}</Cell>
        {codes.map((c) =>
          cell(c, m(p.prices[c]?.credit_eur, c), () => (
            <PriceInput value={draft.packs[p.key].credit} placeholder={reference(Boolean(p.prices[c]), eur?.credit_eur, '')} onChange={(v) => setPack(p.key, { credit: v })} label={`${p.name} ${t('packs.credit')}`} testId={`pack-${p.key}-credit`} />
          )),
        )}
      </Row>,
    );
  }

  return (
    <form onSubmit={save}>
      <Card
        title={editing ? t('prices.editing', { currency: editing }) : t('prices.table')}
        actions={
          editing && (
            <>
              <Button onClick={() => setEditing(null)} data-testid="prices-cancel">
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" busy={busy} data-testid="prices-save">
                {t('common.save')}
              </Button>
            </>
          )
        }
      >
        <p className="mb-3 text-[12px] text-muted">{editing ? t('prices.editHint', { currency: editing }) : t('prices.tableHint')}</p>
        {fail && (
          <div className="mb-3">
            <Alert>{fail}</Alert>
          </div>
        )}
        <Table head={head}>{rows}</Table>
      </Card>
    </form>
  );
}

export function Prices() {
  const { t } = useTranslation();
  const { data, error, loading } = useAsync(() => api.priceTable(), []);
  const [table, setTable] = useState<PriceTable | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => {
    if (data) setTable(data);
  }, [data]);

  const warnings = table
    ? table.currencies
        .filter((c) => c.on_sale && c.code !== EUR)
        .map((c) => ({ code: c.code, names: unpricedOnSale(table, c.code) }))
        .filter((w) => w.names.length > 0)
    : [];

  return (
    <div>
      <PageTitle title={t('prices.title')} subtitle={t('prices.subtitle')} />
      {error && <Alert>{error}</Alert>}
      {loading && !table && <p className="text-[12.5px] text-faint">{t('common.loading')}</p>}
      {table && (
        <div className="flex flex-col gap-4">
          {warnings.map((w) => (
            <Alert key={w.code} tone="warn">
              <span data-testid={`unpriced-${w.code}`}>{t('prices.unpricedOnSale', { currency: w.code, names: w.names.join(', ') })}</span>
            </Alert>
          ))}
          {done && <Alert tone="good">{done}</Alert>}
          <CurrenciesCard
            currencies={table.currencies}
            onSaved={(currencies) => {
              setTable({ ...table, currencies });
              setDone(t('prices.currencySaved'));
            }}
            onDeleted={(currencies) => {
              setTable({ ...table, currencies });
              setDone(t('prices.currencyDeleted'));
            }}
          />
          <PriceTableCard
            table={table}
            onSaved={(next, currency) => {
              setTable(next);
              setDone(t('prices.pricesSaved', { currency }));
            }}
          />
        </div>
      )}
    </div>
  );
}
